import React, { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Radio, Heart, Music4, MessageSquareDot, Users, CalendarClock, BookOpen,
  Play, Menu, Sparkles, ChevronLeft, ChevronRight, X, Check, Flame,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FEATURES = [
  { icon: Radio, label: "Listen 24/7", desc: "Nonstop gospel stations" },
  { icon: Heart, label: "Favorites", desc: "Save & reorder stations" },
  { icon: Music4, label: "Song Requests", desc: "Ask hosts to play a song" },
  { icon: MessageSquareDot, label: "Live Chat", desc: "Fellowship in real time" },
  { icon: Users, label: "Prayer Wall", desc: "Share & pray together" },
  { icon: CalendarClock, label: "Show Reminders", desc: "Never miss a show" },
];

const DESKTOP_STEPS = [
  { sel: "nav-listen", icon: Radio, title: "Listen anytime", body: "Stream gospel, praise & worship stations around the clock. Tap any station to start playing." },
  { sel: "nav-favorites", icon: Heart, title: "Save your favorites", body: "Tap the heart on any station to keep it here — then drag them into the order you love." },
  { sel: "nav-requests", icon: Music4, title: "Request a song", body: "Ask our hosts to play your favorite song live on the air." },
  { sel: "nav-chat", icon: MessageSquareDot, title: "Join the live chat", body: "Fellowship with listeners around the world in real time." },
  { sel: "nav-prayer-wall", icon: Users, title: "Prayer Wall", body: "Share a prayer request and pray over others. We'll let you know when people pray with you." },
  { sel: "nav-schedule", icon: CalendarClock, title: "Shows & reminders", body: "Browse the weekly line-up and tap \u201cRemind me\u201d to get a nudge before a show begins." },
  { sel: "nav-bible", icon: BookOpen, title: "Read the Word", body: "Look up any passage in the built-in Bible reader." },
  { sel: "bottom-player", icon: Play, title: "Your player", body: "Play, pause, save songs, set a sleep timer and see what's playing — always here at the bottom.", optional: true },
  { sel: "streak-badge", icon: Flame, title: "Listening streak", body: "Tune in daily to build your streak. It lives right up here in the top bar.", optional: true },
];

const MOBILE_STEPS = [
  { sel: "mnav-listen", icon: Radio, title: "Listen anytime", body: "Stream gospel stations around the clock. Tap a station to start playing." },
  { sel: "mnav-faves", icon: Heart, title: "Save your favorites", body: "Tap the heart on any station to keep it here — and drag to reorder." },
  { sel: "mnav-chat", icon: MessageSquareDot, title: "Join the live chat", body: "Fellowship with listeners around the world in real time." },
  { sel: "mnav-more", icon: Menu, title: "Everything else", body: "Find Song Requests, Prayer Wall, the Schedule with show reminders, Bible and your profile in the More menu." },
  { sel: "bottom-player", icon: Play, title: "Your player", body: "Play, pause, save songs and set a sleep timer — always here at the bottom.", optional: true },
];

function isVisible(el) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 4 && r.height > 4;
}

export default function OnboardingTutorial() {
  const { user, setUser } = useAuth();
  const [phase, setPhase] = useState(null); // 'welcome' | 'tour' | null
  const [steps, setSteps] = useState([]);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [autoDone, setAutoDone] = useState(false);
  const [firstRun, setFirstRun] = useState(false);

  // Auto-start for brand-new users (once). State-based so React StrictMode's
  // double-invoked effects don't cancel it the way a setTimeout+cleanup would.
  useEffect(() => {
    if (user && user.onboarded === false && !autoDone && phase === null) {
      setAutoDone(true);
      setFirstRun(true);
      setPhase("welcome");
    }
  }, [user, autoDone, phase]);

  // Allow replay from anywhere (e.g. Profile page).
  useEffect(() => {
    const handler = () => { setFirstRun(false); setPhase("welcome"); };
    window.addEventListener("start-tour", handler);
    return () => window.removeEventListener("start-tour", handler);
  }, []);

  const logEvent = (action) => { if (firstRun) api.post("/tour/event", { action }).catch(() => {}); };

  const endTour = async (action) => {
    logEvent(action);
    setPhase(null);
    setIdx(0);
    try {
      const { data } = await api.post("/auth/onboarded");
      if (data?.user) setUser(data.user);
    } catch (e) {}
  };

  const beginTour = () => {
    logEvent("start");
    const isDesktop = isVisible(document.querySelector('[data-testid="nav-listen"]'));
    const base = isDesktop ? DESKTOP_STEPS : MOBILE_STEPS;
    const usable = base.filter((s) => !s.optional || isVisible(document.querySelector(`[data-testid="${s.sel}"]`)));
    if (usable.length === 0) { endTour("finish"); return; }
    setSteps(usable);
    setIdx(0);
    setPhase("tour");
  };

  const current = phase === "tour" ? steps[idx] : null;

  // Track the highlighted element's position.
  useLayoutEffect(() => {
    if (!current) return;
    const update = () => {
      const el = document.querySelector(`[data-testid="${current.sel}"]`);
      if (el) {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
        setRect(el.getBoundingClientRect());
      } else {
        setRect(null);
      }
    };
    update();
    const t = setTimeout(update, 260);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { clearTimeout(t); window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [current]);

  if (phase === "welcome") {
    return createPortal(
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="onboarding-welcome">
        <div className="relative w-full max-w-lg rounded-3xl border border-[#E6B34A]/25 bg-[#0C1338] p-7 shadow-[0_20px_80px_rgba(0,0,0,0.6)] fade-up">
          <button onClick={() => endTour("skip")} data-testid="onboarding-close" className="absolute right-4 top-4 text-zinc-500 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E6B34A]/10 text-[#E6B34A] mb-4">
            <Sparkles className="w-4 h-4" /><span className="overline">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</span>
          </div>
          <h2 className="font-display font-black text-3xl tracking-tight leading-tight">Welcome to <span className="text-[#E6B34A]">In His Image Radio</span></h2>
          <p className="text-zinc-300 mt-2 leading-relaxed">Let's take a quick tour so you can make the most of everything — from live worship to prayer and fellowship.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6">
            {FEATURES.map((f) => (
              <div key={f.label} className="rounded-2xl border border-white/5 bg-white/[0.03] p-3 text-center">
                <div className="w-9 h-9 mx-auto rounded-full bg-[#E6B34A]/12 flex items-center justify-center mb-2"><f.icon className="w-4 h-4 text-[#E6B34A]" /></div>
                <p className="text-sm font-bold leading-tight">{f.label}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5 leading-tight">{f.desc}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-7">
            <button onClick={beginTour} data-testid="onboarding-start" className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">
              <Play className="w-4 h-4" fill="currentColor" /> Take the tour
            </button>
            <button onClick={() => endTour("skip")} data-testid="onboarding-skip" className="inline-flex items-center justify-center gap-2 py-3 px-5 rounded-xl bg-white/5 text-zinc-300 font-semibold hover:bg-white/10 transition-colors">
              Skip for now
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  if (phase === "tour" && current) {
    const vw = window.innerWidth, vh = window.innerHeight;
    const pad = 8;
    const tipW = Math.min(340, vw - 24);
    let holeStyle = null, tipStyle = {};
    if (rect) {
      holeStyle = {
        left: rect.left - pad, top: rect.top - pad,
        width: rect.width + pad * 2, height: rect.height + pad * 2,
      };
      const below = rect.top < vh / 2;
      let left = Math.min(Math.max(12, rect.left), vw - tipW - 12);
      const top = below ? rect.bottom + 14 : undefined;
      const bottom = below ? undefined : vh - rect.top + 14;
      tipStyle = { left, top, bottom, width: tipW };
    } else {
      tipStyle = { left: (vw - tipW) / 2, top: vh / 2 - 80, width: tipW };
    }
    const Icon = current.icon;
    return createPortal(
      <div className="fixed inset-0 z-[100]" data-testid="onboarding-tour">
        {/* Spotlight cutout */}
        {holeStyle ? (
          <div className="absolute rounded-2xl transition-all duration-300 pointer-events-none" style={{ ...holeStyle, boxShadow: "0 0 0 9999px rgba(4,8,26,0.82)", outline: "2px solid #E6B34A", outlineOffset: 2 }} />
        ) : (
          <div className="absolute inset-0" style={{ background: "rgba(4,8,26,0.82)" }} />
        )}
        {/* Tooltip */}
        <div className="absolute rounded-2xl border border-[#E6B34A]/30 bg-[#0C1338] p-5 shadow-[0_20px_60px_rgba(0,0,0,0.6)]" style={tipStyle} data-testid="onboarding-tooltip">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-9 h-9 rounded-full bg-[#E6B34A]/12 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[#E6B34A]" /></div>
            <h3 className="font-display font-extrabold text-lg tracking-tight">{current.title}</h3>
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed">{current.body}</p>
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-1.5" data-testid="onboarding-progress">
              {steps.map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-[#E6B34A]" : "w-1.5 bg-white/20"}`} />
              ))}
            </div>
            <div className="flex items-center gap-2">
              {idx > 0 && (
                <button onClick={() => setIdx((i) => i - 1)} data-testid="onboarding-back" className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-white/5 text-zinc-300 text-sm font-semibold hover:bg-white/10 transition-colors">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
              )}
              {idx < steps.length - 1 ? (
                <button onClick={() => setIdx((i) => i + 1)} data-testid="onboarding-next" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-[#E6B34A] text-[#0A1030] text-sm font-bold hover:bg-[#CF9A34] transition-colors">
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={() => endTour("finish")} data-testid="onboarding-finish" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#E6B34A] text-[#0A1030] text-sm font-bold hover:bg-[#CF9A34] transition-colors">
                  <Check className="w-4 h-4" /> Finish
                </button>
              )}
            </div>
          </div>
          <button onClick={() => endTour("skip")} data-testid="onboarding-tour-skip" className="absolute -top-3 -right-3 w-7 h-7 rounded-full bg-[#141C42] border border-white/10 text-zinc-400 hover:text-white flex items-center justify-center transition-colors" title="Skip tour">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>,
      document.body,
    );
  }

  return null;
}
